// =====================================================
// Journal Entry Type Definitions
// Typed helpers derived from the generated Supabase schema
// =====================================================

import type { Database } from '@/integrations/supabase/types';

type BookingEntryRow = Database['public']['Tables']['booking_entries']['Row'];
type JournalEntryRow = Database['public']['Tables']['journal_entries']['Row'];
type ChartOfAccountRow =
  Database['public']['Tables']['mandate_chart_of_accounts']['Row'];

export type BookingEntryWithAccount = BookingEntryRow & {
  chart_of_accounts?: Pick<
    ChartOfAccountRow,
    'account_number' | 'account_name' | 'account_type'
  > | null;
};

export type JournalEntryWithBookings = JournalEntryRow & {
  booking_entries: BookingEntryWithAccount[];
};

const withEntries = (entry: JournalEntryWithBookings) =>
  entry.booking_entries ?? [];

export const calculateTotalDebit = (
  entry: JournalEntryWithBookings,
): number =>
  withEntries(entry).reduce(
    (sum, booking) => sum + (booking.debit_amount ?? 0),
    0,
  );

export const calculateTotalCredit = (
  entry: JournalEntryWithBookings,
): number =>
  withEntries(entry).reduce(
    (sum, booking) => sum + (booking.credit_amount ?? 0),
    0,
  );

export const calculateTotalVAT = (entry: JournalEntryWithBookings): number =>
  withEntries(entry).reduce(
    (sum, booking) => sum + (booking.vat_amount ?? 0),
    0,
  );

export const getPrimaryDebitAccount = (
  entry: JournalEntryWithBookings,
): BookingEntryWithAccount | null =>
  withEntries(entry).find((booking) => (booking.debit_amount ?? 0) > 0) ??
  null;

export const getPrimaryCreditAccount = (
  entry: JournalEntryWithBookings,
): BookingEntryWithAccount | null =>
  withEntries(entry).find((booking) => (booking.credit_amount ?? 0) > 0) ??
  null;

export const isBalanced = (
  entry: JournalEntryWithBookings,
  tolerance = 0.01,
): boolean => {
  const debit = calculateTotalDebit(entry);
  const credit = calculateTotalCredit(entry);
  return Math.abs(debit - credit) <= tolerance;
};
